import React, { useState, useEffect, useRef } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { useAuth } from '../auth/AuthContext'
import {
  dashboardService,
  PlayerCoincidenceStats,
  ConnectionPair,
  ConversionStats,
} from './dashboardService'
import { evaluationService } from '../evaluations/evaluationService'
import { formsService } from '../health/formsService'
import type { EvaluationResult } from '../evaluations/evaluationTypes'
import type { PlayerDetailStats, PlayerMatchEvolution } from '../../types/sportsStats'
import type { FormObligation } from '../../types/health'
import { LongitudinalEvolutionView } from './LongitudinalEvolutionView'
import {
  Lock,
  User,
  RefreshCw,
  FileText,
  Zap,
  Shield,
  Target,
  ArrowRight,
  Award,
  Layers,
  Activity,
  Calendar,
} from 'lucide-react'

interface IndividualPlayerPerformanceViewProps {
  onOpenDrawer?: (athlete: any) => void
  onNavigateToHealth?: () => void
  isAthleteView?: boolean
  initialAthleteId?: string
  teamId?: string
  athletes?: Array<{
    id: string
    first_name?: string
    last_name?: string
    name?: string
    primary_position?: string
    position?: string
    dorsal_number?: number
    dorsal?: number
    tracking_status?: string
  }>
  camp?: string
  inactive?: boolean
  from?: string
  to?: string
}

export const IndividualPlayerPerformanceView: React.FC<IndividualPlayerPerformanceViewProps> = ({
  onNavigateToHealth,
  isAthleteView = false,
  initialAthleteId,
  teamId: propTeamId,
  athletes: propAthletes,
  camp,
  inactive = false,
  from,
  to,
}) => {
  let user = null
  try {
    const auth = useAuth()
    user = auth?.user ?? null
  } catch {
    user = null
  }
  const teamId = propTeamId || user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  // Lista de atletas para selector (solo para staff)
  const [athletesList, setAthletesList] = useState<Array<{ id: string; name: string; position: string; dorsal: number }>>([])
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>(initialAthleteId || '')

  // Datos reales del atleta seleccionado
  const [playerDetail, setPlayerDetail] = useState<PlayerDetailStats | null>(null)
  const [bodyEvaluations, setBodyEvaluations] = useState<EvaluationResult[]>([])
  const [performanceEvaluations, setPerformanceEvaluations] = useState<EvaluationResult[]>([])
  const [coincidence, setCoincidence] = useState<PlayerCoincidenceStats | null>(null)
  const [connections, setConnections] = useState<ConnectionPair[]>([])
  const [conversions, setConversions] = useState<ConversionStats | null>(null)
  const [pendingObligationsList, setPendingObligationsList] = useState<FormObligation[]>([])
  const [pendingFormsCount, setPendingFormsCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  // Métrica deportiva seleccionada para el gráfico por jornada
  const [matchMetric, setMatchMetric] = useState<'snaps' | 'yards' | 'passing_cmp' | 'flags'>('snaps')

  // Control de concurrencia y peticiones desactualizadas
  const activeRequestId = useRef(0)

  // 1. Cargar lista de atletas o resolver propia identidad
  useEffect(() => {
    let isMounted = true

    async function initAthletes() {
      if (propAthletes !== undefined) {
        const mapped = propAthletes.map((a) => ({
          id: a.id,
          name: a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Atleta',
          position: a.position || a.primary_position || 'ATH',
          dorsal: a.dorsal ?? a.dorsal_number ?? 0,
        }))
        if (!isMounted) return
        setAthletesList(mapped)

        if (isAthleteView) {
          const selfAth = mapped[0]
          if (selfAth) setSelectedAthleteId(selfAth.id)
          else setSelectedAthleteId('')
        } else if (mapped.length > 0) {
          if (!selectedAthleteId || !mapped.some((m) => m.id === selectedAthleteId)) {
            setSelectedAthleteId(mapped[0].id)
          }
        } else {
          setSelectedAthleteId('')
        }
        setLoading(false)
        return
      }

      // Si no vienen por props, resolver contexto en servidor
      try {
        setLoading(true)
        const ctx = await dashboardService.getContext({
          teamId,
          camp,
          inactive,
        })
        if (!isMounted) return
        const mapped = (ctx.athletes || []).map((a) => ({
          id: a.id,
          name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Atleta',
          position: a.primary_position || 'ATH',
          dorsal: a.dorsal_number || 0,
        }))
        setAthletesList(mapped)

        if (isAthleteView) {
          const selfAth = mapped[0]
          if (selfAth) setSelectedAthleteId(selfAth.id)
          else setSelectedAthleteId('')
        } else if (mapped.length > 0) {
          if (!selectedAthleteId || !mapped.some((m) => m.id === selectedAthleteId)) {
            setSelectedAthleteId(mapped[0].id)
          }
        } else {
          setSelectedAthleteId('')
        }
      } catch (err) {
        if (!isMounted) return
        console.warn('Error cargando censo de atletas:', err)
        setAthletesList([])
        setSelectedAthleteId('')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    initAthletes()
    return () => {
      isMounted = false
    }
  }, [teamId, isAthleteView, propAthletes, camp, inactive])

  // 2. Cargar detalle deportivo, evaluaciones longitudinales y estadísticas
  useEffect(() => {
    if (!selectedAthleteId) {
      setPlayerDetail(null)
      setBodyEvaluations([])
      setPerformanceEvaluations([])
      setCoincidence(null)
      setConnections([])
      setConversions(null)
      setPendingFormsCount(null)
      setLoading(false)
      return
    }

    let isMounted = true
    const reqId = ++activeRequestId.current

    async function loadAthleteData() {
      setLoading(true)
      try {
        const filters = {
          eventId: camp || undefined,
          startDate: from || undefined,
          endDate: to || undefined,
        }

        const [
          detailRes,
          bodyRes,
          perfRes,
          coincRes,
          connRes,
          convRes,
          pendingFormsRes,
        ] = await Promise.all([
          dashboardService.getPlayerDetail(teamId, selectedAthleteId, filters).catch(() => null),
          // Cargar todo el histórico longitudinal de evaluaciones para el atleta (sin restringir por sesión única)
          evaluationService.results(teamId, 'body', null, selectedAthleteId).catch(() => []),
          evaluationService.results(teamId, 'performance', null, selectedAthleteId).catch(() => []),
          dashboardService.getPlayerCoincidence(teamId, selectedAthleteId, filters).catch(() => null),
          dashboardService.getConnections(teamId, filters).catch(() => []),
          dashboardService.getConversionStats(teamId).catch(() => null),
          formsService.getAthletePendingObligations(teamId, selectedAthleteId).catch(() => []),
        ])

        if (!isMounted || activeRequestId.current !== reqId) return
        setPlayerDetail(detailRes)
        setBodyEvaluations(bodyRes || [])
        setPerformanceEvaluations(perfRes || [])
        setCoincidence(coincRes)
        setConnections(connRes || [])
        setConversions(convRes)
        setPendingObligationsList(pendingFormsRes || [])
        setPendingFormsCount(pendingFormsRes?.length ?? null)
      } catch (err) {
        if (activeRequestId.current === reqId) {
          console.warn('Error cargando ficha individual:', err)
        }
      } finally {
        if (isMounted && activeRequestId.current === reqId) {
          setLoading(false)
        }
      }
    }

    loadAthleteData()
    return () => {
      isMounted = false
    }
  }, [teamId, selectedAthleteId, camp, from, to])

  // Estado vacío explícito si no hay atletas en el censo
  if (!loading && athletesList.length === 0) {
    return (
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '3rem 1.5rem',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'var(--surface-subtle, #f1f5f9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            color: 'var(--text-muted, #64748b)',
          }}
        >
          <User size={24} />
        </div>
        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
          {isAthleteView ? 'Sin ficha de atleta vinculada' : 'Sin atletas disponibles'}
        </h3>
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
          {isAthleteView
            ? 'Tu cuenta de usuario no tiene asignada una ficha de atleta activa en este equipo. Contacta con el cuerpo técnico federativo.'
            : 'No se encontraron atletas registrados o convocados con los filtros actuales.'}
        </p>
      </div>
    )
  }

  // Estado de carga con spinner y mensaje accesible
  if (loading && !playerDetail) {
    return (
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '3rem 1.5rem',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          color: 'var(--text-muted, #64748b)',
          fontSize: '0.85rem',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <RefreshCw size={18} className="spin" />
          <span>Cargando rendimiento individual del atleta…</span>
        </div>
      </div>
    )
  }

  const currentAthleteInfo = athletesList.find((a) => a.id === selectedAthleteId) || {
    id: selectedAthleteId,
    name: playerDetail?.overview.first_name ? `${playerDetail.overview.first_name} ${playerDetail.overview.last_name}` : 'Atleta',
    position: playerDetail?.overview.primary_position || 'ATH',
    dorsal: playerDetail?.overview.dorsal_number || 0,
  }

  // Conexiones personales del atleta seleccionado
  const personalConnections = connections.filter(
    (c) => c.passer_id === selectedAthleteId || c.receiver_id === selectedAthleteId
  )

  // Conversiones del atleta
  const athleteConversions = conversions?.by_player?.find((p) => p.athlete_id === selectedAthleteId)

  // Datos para el gráfico de evolución por jornada real
  const matchChartData = (playerDetail?.by_match || []).map((m: PlayerMatchEvolution) => {
    let metricVal = 0
    if (matchMetric === 'snaps') metricVal = m.snaps
    else if (matchMetric === 'yards') metricVal = (m.passing_yards || 0) + (m.receiving_yards || 0) + (m.rush_yards || 0)
    else if (matchMetric === 'passing_cmp') metricVal = m.passing_cmp
    else if (matchMetric === 'flags') metricVal = m.flags_ok

    return {
      matchId: m.match_id,
      title: m.match_title,
      label: m.match_title.length > 15 ? m.match_title.slice(0, 14) + '…' : m.match_title,
      date: new Date(m.match_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
      value: metricVal,
      snaps: m.snaps,
      passingCmp: m.passing_cmp,
      passingAtt: m.passing_att,
      passYards: m.passing_yards || 0,
      recYards: m.receiving_yards || 0,
      receptions: m.receptions,
      flagsOk: m.flags_ok,
      sacks: m.sacks,
      ints: m.interceptions,
    }
  })

  // Mejores marcas personales compatibles en pruebas físicas (PRs)
  const personalBests = {
    sprint40: performanceEvaluations
      .filter((e) => e.athlete_id === selectedAthleteId && e.protocol_code === 'SPRINT40' && e.representative !== null)
      .map((e) => Number(e.representative))
      .sort((a, b) => a - b)[0] ?? null,
    sprint10: performanceEvaluations
      .filter((e) => e.athlete_id === selectedAthleteId && e.protocol_code === 'SPRINT10' && e.representative !== null)
      .map((e) => Number(e.representative))
      .sort((a, b) => a - b)[0] ?? null,
    cone3: performanceEvaluations
      .filter((e) => e.athlete_id === selectedAthleteId && e.protocol_code === 'CONE3' && e.representative !== null)
      .map((e) => Number(e.representative))
      .sort((a, b) => a - b)[0] ?? null,
    jump: performanceEvaluations
      .filter((e) => e.athlete_id === selectedAthleteId && e.protocol_code === 'JUMP' && e.representative !== null)
      .map((e) => Number(e.representative))
      .sort((a, b) => b - a)[0] ?? null,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* E. ACCESO A FORMULARIOS (Llamada compacta a la acción) */}
      {pendingFormsCount !== null && (
        <div
          data-testid="pending-forms-banner"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            borderRadius: '8px',
            background: pendingFormsCount > 0 ? '#fffbeb' : '#f0fdf4',
            border: pendingFormsCount > 0 ? '1px solid #fef3c7' : '1px solid #bbf7d0',
            color: pendingFormsCount > 0 ? '#92400e' : '#166534',
            fontSize: '0.82rem',
            fontWeight: 600,
            gap: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={16} color={pendingFormsCount > 0 ? '#d97706' : '#16a34a'} />
            <span>
              {pendingFormsCount > 0
                ? pendingObligationsList.some((o) => o.form_type === 'camp_daily')
                  ? `Tienes el check-in diario de concentración pendiente de entrega${pendingFormsCount > 1 ? ` (+ ${pendingFormsCount - 1} semanal)` : ''}`
                  : `Tienes ${pendingFormsCount} ${pendingFormsCount === 1 ? 'formulario pendiente' : 'formularios pendientes'} de bienestar y seguimiento semanal`
                : 'Estás al día con todos tus formularios de bienestar y seguimiento'}
            </span>
          </div>

          {pendingFormsCount > 0 && onNavigateToHealth && (
            <button
              type="button"
              onClick={onNavigateToHealth}
              style={{
                padding: '4px 12px',
                fontSize: '0.76rem',
                fontWeight: 700,
                color: '#ffffff',
                background: '#b45309',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Completar
            </button>
          )}
        </div>
      )}

      {/* A. CABECERA PERSONAL DEL ATLETA */}
      <div
        style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'var(--brand-red, #b91c1c)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              boxShadow: '0 2px 8px rgba(185, 28, 28, 0.35)',
            }}
          >
            #{currentAthleteInfo.dorsal || '—'}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  color: 'var(--text-main, #0f172a)',
                  letterSpacing: '-0.02em',
                }}
              >
                {currentAthleteInfo.name}
              </h2>
              <span
                style={{
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: 'rgba(217, 119, 6, 0.12)',
                  color: '#b45309',
                }}
              >
                {currentAthleteInfo.position}
              </span>
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: '#f1f5f9',
                  color: '#475569',
                }}
              >
                Selección Española 5v5
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)', marginTop: '4px' }}>
              <span>{playerDetail?.overview.participation.total_snaps || 0} snaps disputados en el ciclo</span>
              <span>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={13} color="#64748b" />
                Periodo visible: <strong>{from || 'Inicio'}</strong> al <strong>{to || 'Hoy'}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Control de Privacidad / Selector de Atleta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isAthleteView ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--surface-subtle, #f8fafc)',
                border: '1px solid var(--surface-border, #cbd5e1)',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '0.78rem',
                color: 'var(--text-secondary, #475569)',
              }}
            >
              <Lock size={13} color="var(--text-muted, #64748b)" />
              <span>Vista propia protegida (Solo tus datos autorizados)</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
                Cambiar atleta:
              </span>
              <select
                data-testid="athlete-selector"
                aria-label="Cambiar atleta"
                value={selectedAthleteId}
                onChange={(e) => setSelectedAthleteId(e.target.value)}
                style={{
                  fontSize: '0.82rem',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--surface-border, #cbd5e1)',
                  background: 'var(--surface-card, #ffffff)',
                  color: 'var(--text-main, #0f172a)',
                  maxWidth: '240px',
                }}
              >
                {athletesList.map((a) => (
                  <option key={a.id} value={a.id}>
                    #{a.dorsal || '—'} {a.name} ({a.position})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* B. KPIS PERSONALES (Jerarquizados por actividad real - Hydra Maintained) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.85rem' }}>
        {/* KPI 1: Participación en Campo */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <Activity size={15} color="#0284c7" />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
              Participación en Campo
            </span>
          </div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
            {playerDetail?.overview.participation.total_snaps || 0}{' '}
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>snaps totales</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
            {playerDetail?.overview.participation.offense_snaps || 0} ataque · {playerDetail?.overview.participation.defense_snaps || 0} defensa
          </div>
        </div>

        {/* KPI 2: Pase (Visible si el jugador lanza o es QB - Hydra) */}
        {playerDetail?.overview?.passing && playerDetail.overview.passing.attempts > 0 && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Zap size={15} color="#f59e0b" />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Función de Pase
              </span>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {playerDetail.overview.passing.completions}/{playerDetail.overview.passing.attempts}{' '}
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#15803d' }}>
                ({playerDetail.overview.passing.comp_pct ?? '—'}%)
              </span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              {playerDetail.overview.passing.yards || 0} yd · {playerDetail.overview.passing.tds} TD · {playerDetail.overview.passing.interceptions} INT
            </div>
          </div>
        )}

        {/* KPI 3: Recepción (Visible si tiene targets o recepciones) */}
        {playerDetail?.overview?.receiving && (playerDetail.overview.receiving.targets > 0 || playerDetail.overview.receiving.receptions > 0) && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Target size={15} color="#ea580c" />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Función de Recepción
              </span>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {playerDetail.overview.receiving.receptions}/{playerDetail.overview.receiving.targets}{' '}
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>rec</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              {playerDetail.overview.receiving.yards || 0} yd · {playerDetail.overview.receiving.tds} TD · {playerDetail.overview.receiving.drops} drops
            </div>
          </div>
        )}

        {/* KPI 4: Carrera (Visible si tiene intentos de carrera) */}
        {playerDetail?.overview?.rushing && playerDetail.overview.rushing.attempts > 0 && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <ArrowRight size={15} color="#16a34a" />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Función de Carrera
              </span>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
              {playerDetail.overview.rushing.attempts} <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>carreras</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              {playerDetail.overview.rushing.yards || 0} yd ganadas · {playerDetail.overview.rushing.tds} TD anotados
            </div>
          </div>
        )}

        {/* KPI 5: Defensa (Visible si tiene snaps defensivos o jugadas de defensa) */}
        {playerDetail?.overview?.defending &&
          (playerDetail.overview.defending.flags_success > 0 ||
            playerDetail.overview.defending.sacks > 0 ||
            playerDetail.overview.defending.interceptions > 0 ||
            playerDetail.overview.participation.defense_snaps > 0) && (
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <Shield size={15} color="#3b82f6" />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Aportación Defensiva
                </span>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                {playerDetail.overview.defending.flags_success}{' '}
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>
                  flags {playerDetail.overview.defending.flag_pct !== null ? `(${playerDetail.overview.defending.flag_pct}%)` : ''}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                {playerDetail.overview.defending.sacks} sacks · {playerDetail.overview.defending.pressures} presiones · {playerDetail.overview.defending.interceptions} INT
              </div>
            </div>
          )}

        {/* KPI 6: Conversiones (Separadas explícitamente del scrimmage ordinario) */}
        {athleteConversions && (
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Award size={15} color="#8b5cf6" />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                Conversiones Extra
              </span>
            </div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
              +{athleteConversions.points} <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>pts conseguidos</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
              {athleteConversions.made} anotadas en {athleteConversions.attempts} intentos
            </div>
          </div>
        )}
      </div>

      {/* C. GRÁFICOS PROTAGONISTAS */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Gráfico 1: Evolución por Jornada Real */}
        <div
          style={{
            background: 'var(--surface-card, #ffffff)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '10px',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Evolución por Jornada Real
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
                Rendimiento deportivo partido a partido en concentraciones registradas
              </p>
            </div>

            {/* Selector de Métrica del Gráfico */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Métrica:</span>
              <select
                value={matchMetric}
                onChange={(e) => setMatchMetric(e.target.value as any)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.78rem',
                  background: '#ffffff',
                }}
              >
                <option value="snaps">Snaps disputados</option>
                <option value="yards">Yardas totales producidas</option>
                <option value="passing_cmp">Pases completos</option>
                <option value="flags">Flags conseguidos</option>
              </select>
            </div>
          </div>

          {matchChartData.length > 0 ? (
            <div style={{ width: '100%', height: '220px', marginTop: '0.5rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={matchChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#0a1320', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.75rem' }}
                    formatter={(val: any, _name: any, item: any) => [
                      `${val} (${item.payload.snaps} snaps, ${item.payload.passingCmp}/${item.payload.passingAtt} pases, ${item.payload.flagsOk} flags)`,
                      item.payload.title,
                    ]}
                  />
                  <Bar dataKey="value" fill="var(--brand-red, #b91c1c)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
              Sin partidos registrados en este periodo con participación del atleta.
            </div>
          )}
        </div>

        {/* Gráfico 2: Conexiones Personales Autorizadas */}
        {personalConnections.length > 0 && (
          <div
            style={{
              background: 'var(--surface-card, #ffffff)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              borderRadius: '10px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Conexiones Personales en Campo
            </h3>
            <p style={{ margin: '0 0 0.85rem', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Parejas de pase en las que participas directamente como lanzador o receptor
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
              {personalConnections.map((c) => (
                <div
                  key={`${c.passer_id}:${c.receiver_id}`}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                      {c.passer_name} ➔ {c.receiver_name}
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#ea580c' }}>
                      {c.tds} TD
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: '#64748b' }}>
                    <span>
                      {c.completions}/{c.targets} ({c.comp_pct ?? 0}%)
                    </span>
                    <span>{c.yards} yardas conseguidas</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gráfico 3: Impacto de Coincidencia en Campo */}
        {coincidence && (
          <div
            style={{
              background: 'var(--surface-card, #ffffff)',
              border: '1px solid var(--surface-border, #e2e8f0)',
              borderRadius: '10px',
              padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Layers size={16} color="#0284c7" />
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Rendimiento del Equipo al Coincidir en Campo
              </h3>
            </div>
            <p style={{ margin: '0 0 0.85rem', fontSize: '0.78rem', color: 'var(--text-secondary, #475569)' }}>
              Comparativa del equipo con y sin {currentAthleteInfo.name} en el quinteto. Muestra oportunidades reales sin asumir causalidad aislada.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px', fontSize: '0.78rem' }}>
              <div style={{ background: '#f0fdf4', padding: '10px 12px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 700, color: '#166534', marginBottom: '6px' }}>CON el jugador en el quinteto</div>
                <div>Snaps: <strong>{coincidence.with_player.snaps}</strong></div>
                <div>Éxito de jugada: <strong>{coincidence.with_player.success_rate ?? '—'}%</strong></div>
                <div>Ganancia / Snap: <strong>{coincidence.with_player.yards_per_snap ?? '—'} yd</strong></div>
                <div>Touchdowns anotados: <strong>{coincidence.with_player.tds}</strong></div>
              </div>

              <div style={{ background: '#fef2f2', padding: '10px 12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: '6px' }}>SIN el jugador en el quinteto</div>
                <div>Snaps: <strong>{coincidence.without_player.snaps}</strong></div>
                <div>Éxito de jugada: <strong>{coincidence.without_player.success_rate ?? '—'}%</strong></div>
                <div>Ganancia / Snap: <strong>{coincidence.without_player.yards_per_snap ?? '—'} yd</strong></div>
                <div>Touchdowns anotados: <strong>{coincidence.without_player.tds}</strong></div>
              </div>
            </div>
          </div>
        )}

        {/* Gráfico 4: «MI EVOLUCIÓN LONGITUDINAL» (Sustituye al antiguo comparador de sesiones) */}
        <LongitudinalEvolutionView
          athleteId={selectedAthleteId}
          athleteName={currentAthleteInfo.name}
          bodyEvaluations={bodyEvaluations}
          performanceEvaluations={performanceEvaluations}
          from={from}
          to={to}
        />
      </div>

      {/* D. CONTEXTO DE PROGRESO Y MARCAS PERSONALES */}
      <div
        style={{
          background: 'var(--surface-subtle, #f8fafc)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.25rem',
        }}
      >
        <h4 style={{ margin: '0 0 6px', fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
          Contexto Metodológico y Marcas Personales (PRs)
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', margin: '0.75rem 0' }}>
          <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.68rem', color: '#64748b' }}>Mejor Sprint 40 yd</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              {personalBests.sprint40 !== null ? `${personalBests.sprint40} s` : 'Sin registro'}
            </div>
          </div>

          <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.68rem', color: '#64748b' }}>Mejor Sprint 10 yd</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              {personalBests.sprint10 !== null ? `${personalBests.sprint10} s` : 'Sin registro'}
            </div>
          </div>

          <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.68rem', color: '#64748b' }}>Mejor Salto Horizontal</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              {personalBests.jump !== null ? `${personalBests.jump} cm` : 'Sin registro'}
            </div>
          </div>

          <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.68rem', color: '#64748b' }}>Mejor 3-Cone Agilidad</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              {personalBests.cone3 !== null ? `${personalBests.cone3} s` : 'Sin registro'}
            </div>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>
          ℹ️ <strong>Rigor estadístico Team Spain:</strong> No se califica como mejora deportiva una diferencia explicada únicamente por mayor número de partidos disputados o volumen de minutos. La evolución longitudinal de pruebas físicas requiere comparar protocolos idénticos.
        </p>
      </div>
    </div>
  )
}

export default IndividualPlayerPerformanceView
